import React, { useState } from 'react';

const ControlDock = ({
    totalMonths,
    currentMonth,
    events,
    onMonthSelect,
    currentZoom,
    onZoomChange,
    isPlaying,
    onTogglePlay,
    startYear,
    endYear,
    currentYear,
    onYearSelect,
    flags
}) => {
    const [hoveredMonth, setHoveredMonth] = useState(null);

    // Calculate progress for the timeline bar
    const progress = (currentMonth / (totalMonths - 1)) * 100;

    const getMonthYearLabel = (monthIndex) => {
        const year = startYear + Math.floor(monthIndex / 12);
        const month = monthIndex % 12;
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[month]} ${year}`;
    };

    return (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-4 w-full max-w-md px-4 pointer-events-none">

            {/* Floating Dock */}
            <div className="bg-[#111]/95 backdrop-blur-md border border-white/10 p-1.5 shadow-2xl flex items-center gap-1 pointer-events-auto transition-all duration-300 ease-out hover:bg-[#111] hover:border-white/20">

                {/* Play/Pause Button */}
                {flags.autoPlay && (
                    <button
                        onClick={onTogglePlay}
                        className={`
              w-10 h-10 flex items-center justify-center transition-all relative
              ${isPlaying
                                ? 'bg-[#F5B50A] text-black'
                                : 'text-white/70 hover:text-white hover:bg-white/10'}
            `}
                        title={isPlaying ? "Pause" : "Auto Play"}
                    >
                        {isPlaying ? (
                            <>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <rect x="6" y="4" width="4" height="16" />
                                    <rect x="14" y="4" width="4" height="16" />
                                </svg>
                                {/* Rotating indicator */}
                                <div className="absolute -top-1 -right-1 w-2 h-2 animate-spin-slow">
                                    <div className="w-full h-full border border-black border-t-transparent rounded-full" style={{ animation: 'spin 1s linear infinite' }} />
                                </div>
                            </>
                        ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M5 3l14 9-14 9V3z" />
                            </svg>
                        )}
                    </button>
                )}

                {/* Divider */}
                {flags.autoPlay && <div className="w-px h-6 bg-white/10 mx-1" />}

                {/* Center Control Area - Swaps based on active interaction */}
                <div className="flex items-center relative h-10">

                    {/* Timeline Scrubber (Default) */}
                    <div className="flex items-center gap-3 px-2 min-w-[200px]">
                        <span className="text-[10px] font-bold text-white/50 tabular-nums tracking-wider">{startYear}</span>

                        <div
                            className="relative flex-1 h-1.5 bg-white/10 cursor-pointer group"
                            onClick={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const x = e.clientX - rect.left;
                                const pct = Math.max(0, Math.min(1, x / rect.width));
                                onMonthSelect(Math.floor(pct * (totalMonths - 1)));
                            }}
                            onMouseMove={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const x = e.clientX - rect.left;
                                const pct = Math.max(0, Math.min(1, x / rect.width));
                                setHoveredMonth(Math.floor(pct * (totalMonths - 1)));
                            }}
                            onMouseLeave={() => setHoveredMonth(null)}
                        >
                            {/* Hover Tooltip */}
                            {hoveredMonth !== null && (
                                <div
                                    className="absolute bottom-full left-0 mb-3 -translate-x-1/2 bg-[#F5B50A] text-black text-[10px] font-bold px-2 py-1 whitespace-nowrap pointer-events-none"
                                    style={{ left: `${(hoveredMonth / (totalMonths - 1)) * 100}%` }}
                                >
                                    {getMonthYearLabel(hoveredMonth)}
                                    {/* Arrow */}
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#F5B50A]" />
                                </div>
                            )}

                            {/* Event Markers on Track */}
                            {Object.keys(events).map(idx => (
                                <div
                                    key={idx}
                                    className="absolute top-0 bottom-0 w-0.5 bg-white/30"
                                    style={{ left: `${(idx / (totalMonths - 1)) * 100}%` }}
                                />
                            ))}

                            {/* Progress Bar */}
                            <div
                                className="absolute top-0 left-0 bottom-0 bg-[#F5B50A] transition-all duration-100"
                                style={{ width: `${progress}%` }}
                            />

                            {/* Thumb */}
                            <div
                                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white shadow-lg transform scale-0 group-hover:scale-100 transition-transform duration-200"
                                style={{ left: `${progress}%`, marginLeft: '-6px' }}
                            />
                        </div>

                        <span className="text-[10px] font-bold text-white/50 tabular-nums tracking-wider">{endYear}</span>
                    </div>

                </div>

                {/* Divider */}
                {(flags.zoomControls || flags.quickJump) && <div className="w-px h-6 bg-white/10 mx-1" />}

                {/* Right Actions */}
                <div className="flex items-center gap-1">
                    {flags.zoomControls && (
                        <button
                            onClick={() => {
                                // Cycle zoom levels: 12 -> 36 -> 100 -> 12
                                const nextZoom = currentZoom === 12 ? 36 : currentZoom === 36 ? 100 : 12;
                                onZoomChange(nextZoom);
                            }}
                            className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-all"
                            title="Change View (Year/Qtr/Month)"
                        >
                            <span className="text-[10px] font-bold uppercase tracking-wider">
                                {currentZoom === 12 ? 'Yr' : currentZoom === 36 ? 'Qtr' : 'Mo'}
                            </span>
                        </button>
                    )}

                    {flags.quickJump && (
                        <button
                            onClick={() => {
                                // Simple toggle for now, could expand a menu
                                const nextYear = currentYear >= endYear ? startYear : currentYear + 1;
                                onYearSelect(nextYear);
                            }}
                            className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-all"
                            title="Jump to Next Year"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="18" />
                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                        </button>
                    )}
                </div>

            </div>
        </div>
    );
};

export default ControlDock;
